'use client';

import { invariant } from '@ifixit/helpers';
import { Slot } from '@radix-ui/react-slot';
import {
   type ComponentProps,
   type FocusEvent,
   type KeyboardEvent,
   type MouseEvent,
   type PointerEvent,
   type ReactNode,
   useCallback,
   use,
   useEffect,
   useId,
   useMemo,
   useRef,
   useState,
} from 'react';
import { useControllableState } from '../hooks/use-controllable-state';
import { useStableCallback } from '../hooks/use-stable-callback';
import { assignRef } from '../lib/assign-ref';
import {
   DropdownMenuContext,
   type DropdownMenuInternals,
   InternalsContext,
   type OpenFocus,
   OpenMenuItemsContext,
   focusInitial,
   listenForDismiss,
   useDropdownMenu,
   useInternals,
} from '../lib/dropdown-menu-context';
import { cn } from '@ifixit/ui/lib/utils';
import type {
   DropdownMenuContent as OpenMenuContent,
   DropdownMenuItem as OpenMenuItem,
   DropdownMenuRadioGroup as OpenMenuRadioGroup,
   DropdownMenuRadioItem as OpenMenuRadioItem,
} from './dropdown-menu-eager';

type OpenMenu = typeof import('./dropdown-menu-eager');

/**
 * A menu button (WAI-ARIA APG). Opening moves focus into the menu, the arrow
 * keys move it from item to item, and closing hands it back to the trigger.
 * The menu renders in place and is lifted into the top layer with the Popover
 * API, which keeps it inside whatever dialog, drawer or sheet owns the trigger
 * while escaping their clipping and transforms.
 *
 * The code an open menu runs (placement, keyboard handling, the items) is
 * fetched the first time someone points at, focuses or opens a menu, so a
 * page where nobody reaches for one never downloads it. `preload` fetches it
 * when the menu mounts instead. Importing from `dropdown-menu-eager` bundles
 * it with the page, for a menu that must open without waiting for a fetch.
 *
 * A menu open on the first render (`defaultOpen`) is not in the server HTML
 * until its code arrives; import `dropdown-menu-eager` for one that must be.
 */

interface DropdownMenuProps {
   open?: boolean;
   defaultOpen?: boolean;
   onOpenChange?: (open: boolean) => void;
   preload?: boolean;
   children?: ReactNode;
}

function DropdownMenu({
   open: openProp,
   defaultOpen = false,
   onOpenChange,
   preload = false,
   children,
}: DropdownMenuProps) {
   // The hook's own onChange only fires in controlled mode; setOpen below
   // notifies uncontrolled consumers too.
   const [open, setOpenState] = useControllableState({
      prop: openProp,
      defaultProp: defaultOpen,
   });
   const triggerId = useId();
   const menuId = useId();
   const internalsRef = useRef<DropdownMenuInternals>({
      trigger: null,
      menu: null,
      openFocus: 'menu',
      search: '',
      searchTimer: 0,
   });

   // Both closes of one click land before React re-renders, so `open` cannot
   // tell the second apart: an item's onSelect that moves focus out closes
   // the menu from the focusin listener, then the click closes it again.
   const reportedRef = useRef<boolean | null>(null);

   const setOpen = useStableCallback((next: boolean) => {
      if (next === reportedRef.current) return;
      reportedRef.current = next;
      queueMicrotask(() => {
         reportedRef.current = null;
      });
      setOpenState(next);
      onOpenChange?.(next);
   });

   const openWith = useStableCallback((focus: OpenFocus) => {
      internalsRef.current.openFocus = focus;
      if (open) focusInitial(internalsRef.current);
      else setOpen(true);
   });

   const close = useStableCallback(({ returnFocus }: { returnFocus: boolean }) => {
      const internals = internalsRef.current;
      const { menu, trigger } = internals;
      const focused = document.activeElement;
      // An item's handler that moved focus on purpose (into an editor, say)
      // keeps it there.
      const focusInMenu = focused === document.body || menu?.contains(focused);
      if (returnFocus && focusInMenu) trigger?.focus({ preventScroll: true });
      // A menu closed before its code arrived never ran the content's
      // cleanup, which resets this too.
      internals.openFocus = 'menu';
      setOpen(false);
   });

   useEffect(() => {
      if (preload) void fetchOpenMenu();
   }, [preload]);

   // Carpenter builds without the React Compiler; unmemoized, every part of
   // every menu re-renders whenever its owner does.
   const context = useMemo(
      () => ({ open, menuId, triggerId, openWith, close }),
      [open, menuId, triggerId, openWith, close]
   );

   return (
      <DropdownMenuContext value={context}>
         <InternalsContext value={internalsRef}>{children}</InternalsContext>
      </DropdownMenuContext>
   );
}

function DropdownMenuTrigger({
   asChild = false,
   ref,
   onClick,
   onKeyDown,
   onKeyUp,
   onPointerEnter,
   onFocus,
   ...props
}: ComponentProps<'button'> & { asChild?: boolean }) {
   const menu = useDropdownMenu();
   const internalsRef = useInternals();
   const setTriggerNode = useCallback(
      (node: HTMLButtonElement | null) => {
         internalsRef.current.trigger = node;
         assignRef(ref, node);
      },
      [internalsRef, ref]
   );
   const Comp = asChild ? Slot : 'button';

   return (
      <Comp
         ref={setTriggerNode}
         id={menu.triggerId}
         type="button"
         aria-haspopup="menu"
         aria-expanded={menu.open}
         aria-controls={menu.open ? menu.menuId : undefined}
         data-slot="dropdown-menu-trigger"
         data-state={menu.open ? 'open' : 'closed'}
         onClick={(event: MouseEvent<HTMLButtonElement>) => {
            onClick?.(event);
            if (event.defaultPrevented) return;
            if (menu.open) menu.close({ returnFocus: true });
            // A click without a press, from assistive tech or a script, gets
            // the keyboard's treatment.
            else menu.openWith(event.detail === 0 ? 'first' : 'menu');
         }}
         onKeyDown={(event: KeyboardEvent<HTMLButtonElement>) => {
            onKeyDown?.(event);
            if (event.defaultPrevented) return;
            switch (event.key) {
               case 'Enter':
               case ' ': {
                  if (menu.open) menu.close({ returnFocus: true });
                  else menu.openWith('first');
                  break;
               }
               case 'ArrowDown': {
                  menu.openWith('first');
                  break;
               }
               case 'ArrowUp': {
                  menu.openWith('last');
                  break;
               }
               default: {
                  return;
               }
            }
            event.preventDefault();
         }}
         onKeyUp={(event: KeyboardEvent<HTMLButtonElement>) => {
            onKeyUp?.(event);
            // Firefox clicks a button on Space keyup even when the keydown
            // was handled, which would toggle the menu straight back.
            if (event.key === ' ') event.preventDefault();
         }}
         // A touch fires pointerenter before its press too.
         onPointerEnter={(event: PointerEvent<HTMLButtonElement>) => {
            onPointerEnter?.(event);
            void fetchOpenMenu();
         }}
         onFocus={(event: FocusEvent<HTMLButtonElement>) => {
            onFocus?.(event);
            void fetchOpenMenu();
         }}
         {...props}
      />
   );
}

function DropdownMenuContent(props: ComponentProps<typeof OpenMenuContent>) {
   const { open, close } = useDropdownMenu();
   const internalsRef = useInternals();
   const [fetched, setFetched] = useState(openMenu);
   const loaded = fetched ?? openMenu;

   useEffect(() => {
      if (!open || loaded) return;
      let current = true;
      void fetchOpenMenu({ retry: true }).then(module => {
         if (!current) return;
         if (module) setFetched(module);
         else close({ returnFocus: false });
      });
      // While the code is on its way, a press elsewhere or Escape still
      // closes the menu.
      const stopDismissing = listenForDismiss(internalsRef.current, close);
      return () => {
         current = false;
         stopDismissing();
      };
   }, [open, loaded, close, internalsRef]);

   const Content = loaded?.DropdownMenuContent;
   return Content ? <Content {...props} /> : null;
}

function DropdownMenuItem(props: ComponentProps<typeof OpenMenuItem>) {
   const { Item } = useOpenMenuItems();
   return <Item {...props} />;
}

function DropdownMenuRadioGroup(props: ComponentProps<typeof OpenMenuRadioGroup>) {
   const { RadioGroup } = useOpenMenuItems();
   return <RadioGroup {...props} />;
}

function DropdownMenuRadioItem(props: ComponentProps<typeof OpenMenuRadioItem>) {
   const { RadioItem } = useOpenMenuItems();
   return <RadioItem {...props} />;
}

/** Items only render inside an open menu, whose content provides them. */
function useOpenMenuItems() {
   const items = use(OpenMenuItemsContext);
   invariant(items, 'DropdownMenu items must be rendered inside a DropdownMenuContent');
   return items;
}

function DropdownMenuGroup(props: ComponentProps<'div'>) {
   return <div role="group" data-slot="dropdown-menu-group" {...props} />;
}

function DropdownMenuLabel({ className, ...props }: ComponentProps<'div'>) {
   return (
      <div
         data-slot="dropdown-menu-label"
         className={cn('px-2 py-1.5 text-sm font-medium', className)}
         {...props}
      />
   );
}

function DropdownMenuSeparator({ className, ...props }: ComponentProps<'div'>) {
   return (
      <div
         role="separator"
         aria-orientation="horizontal"
         data-slot="dropdown-menu-separator"
         className={cn('bg-border -mx-1 my-1 h-px', className)}
         {...props}
      />
   );
}

function DropdownMenuShortcut({ className, ...props }: ComponentProps<'span'>) {
   return (
      <span
         data-slot="dropdown-menu-shortcut"
         className={cn('text-muted-foreground ml-auto text-xs tracking-widest', className)}
         {...props}
      />
   );
}

let openMenu: OpenMenu | undefined;
let openMenuRequest: Promise<OpenMenu | null> | undefined;
let openMenuFailed = false;

/**
 * Fetches the code of an open menu once per page. After a failed fetch (a
 * deploy that replaced the file, a dropped connection), only opening a menu
 * fetches again, so a page left open across a deploy does not refetch and
 * report on every hover. Only the first failure is reported.
 */
function fetchOpenMenu({ retry = false } = {}) {
   if (openMenuFailed && !retry) return openMenuRequest ?? Promise.resolve(null);
   openMenuRequest ??= importOpenMenu();
   return openMenuRequest;
}

async function importOpenMenu() {
   try {
      openMenu = await import('./dropdown-menu-eager');
      return openMenu;
   } catch (error) {
      openMenuRequest = undefined;
      if (!openMenuFailed) globalThis.reportError?.(error);
      openMenuFailed = true;
      return null;
   }
}

export {
   DropdownMenu,
   DropdownMenuContent,
   DropdownMenuGroup,
   DropdownMenuItem,
   DropdownMenuLabel,
   DropdownMenuRadioGroup,
   DropdownMenuRadioItem,
   DropdownMenuSeparator,
   DropdownMenuShortcut,
   DropdownMenuTrigger,
};
